import { useFetchUserProfileQuery } from "@/api/clubsApi";
import { Icon } from "@/components/ui/Icon";
import { userDisplayName } from "@tinyclub/shared/utils";

interface PostPromptProps {
  clubId: string;
  currentUserId: string;
  onClick: () => void;
}

/**
 * PostPrompt - A clickable cell showing user avatar, name, and "Post something..." placeholder.
 *
 * Layout per Figma:
 * [Avatar 32x32] | [Name]
 *                | [Post something...]
 */
export function PostPrompt({ currentUserId, onClick }: PostPromptProps) {
  const { data: profile } = useFetchUserProfileQuery(currentUserId);

  const displayName = profile ? userDisplayName(profile) : "You";

  return (
    <button
      onClick={onClick}
      className="w-full px-5 py-4 text-left"
    >
      {/* Two-column layout: avatar + content */}
      <div className="flex gap-4 items-start">
        {/* Avatar 32x32 with IRL adornment support */}
        <div
          className="rounded-full overflow-hidden bg-surface-2 flex-shrink-0 border border-border-tertiary"
          style={{ width: 32, height: 32 }}
        >
          {profile?.avatar_small || profile?.avatar_medium ? (
            <img
              src={profile.avatar_small || profile.avatar_medium || ""}
              alt={displayName}
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <Icon name="user" size={14} className="text-text-tertiary" />
            </div>
          )}
        </div>

        {/* Right column - Name + Prompt */}
        <div className="flex-1 min-w-0 flex flex-col gap-1.5">
          {/* Name row */}
          <p className="text-body-2-medium text-text-primary">
            {displayName}
          </p>
          {/* Post something... prompt */}
          <p className="text-body-2 text-text-tertiary">
            Post something...
          </p>
        </div>
      </div>
    </button>
  );
}
